import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { FeasibilityRule } from 'src/app/models/FeasibilityRule';
import { FeasibilityRuleSimple } from 'src/app/models/FeasibilityRuleSimple';
import { FreelancerFeasibilityRequest } from 'src/app/models/FreelancerFeasibilityRequest';
import { FreelancerFeasibilityResult } from 'src/app/models/FreelancerFeasibilityResult';
import { SaveJsonRequest } from 'src/app/models/SaveJsonRequest';
import { FEASIBILITY_JSON_FILE_NAME, OperatorEnum } from 'src/app/shared/constants/common.constants';
import { FreelancerRequestDetailService } from '../freelancer-request-detail.service';

@Injectable({
    providedIn: 'root'
})
export class FreelancerRequestDetailFeasibilityService {
    feasibilityForm: FormGroup;
    isFeasible: boolean | undefined;
    ruleFeasibilityResults: FeasibilityRule[] = [];

    freelancerFeasibilityResult: FreelancerFeasibilityResult = {
        odIncomeUnroundedLimit: 0,
        odIncomeRoundedLimit: 0,
        percentage: 0,
        pass: false,
        obQuarterlyIncomeUnroundedLimit: 0,
        obQuarterlyIncomeRoundedLimit: 0,
        ok: false,
        ultimateRoundedLimit: 0
    };

    constructor(
		private readonly apiService: ApiService,
		private readonly freelancerRequestDetailService: FreelancerRequestDetailService,
		private readonly datePipe: DatePipe,
		private readonly toastService: ToastService,
        private readonly translateService: TranslateService
    ) {
        this.prepareFeasibilityForm();
    }

    private prepareFeasibilityForm(): void {
        this.feasibilityForm = new FormGroup({
            IngresosElegibles: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            GastosComprometidos: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            ScoreOpenBanking: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // CodigoReglaOB1: new FormControl(null, []),
            // CodigoReglaOB2: new FormControl(null, []),
            ImporteDeudaTotalCirculanteCIRBE: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            ImporteMoraCIRBE: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // DescubiertosCuenta: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            IngresosRecurrentesTrimestrales: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // NumeroRecobrosUltimoAno: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // ImporteMedioGastosApuestas: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // CantidadMesesGastosApuestas: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // VariacionCaidaIngresos: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // NumeroPagosPorFinancierasMensualmente: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // SaldoMedioMinimoMensual6Meses: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            // LimiteCreditoAsignar: new FormControl(null, [Validators.required, this.positiveNumberValidator()]),
            ViabilidadOpenBanking: new FormControl(false, [Validators.required])
        });
    }

    resetFeasibility(): void {
        this.feasibilityForm.reset();
        this.feasibilityForm.get('ViabilidadOpenBanking').setValue(false);
        this.isFeasible = undefined;
        this.ruleFeasibilityResults = [];
        // this.resetCreditAndLimits();
        this.resetFreelancerFeasibilityResult();
    }

    getRules(): Observable<FeasibilityRule[]> {
        const promise = this.apiService.get<FeasibilityRule[]>('rules');
        return from(promise);
    }

    validateRule(rule: FeasibilityRule, request: any): boolean {
        switch (rule.operator) {
            case OperatorEnum.Equals:
                return this.validateEquals(request.value, rule.value);
            case OperatorEnum.NotEquals:
                return !this.validateEquals(request.value, rule.value);
            case OperatorEnum.LessThan:
                return this.validateLessThan(request.value, rule.value);
            case OperatorEnum.LessThanOrEqual:
                return this.validateLessOrEqualThan(request.value, rule.value);
            case OperatorEnum.GreaterThan:
                return this.validateGreaterThan(request.value, rule.value);
            case OperatorEnum.GreaterThanOrEqual:
                return this.validateGreaterOrEqualThan(request.value, rule.value);
            case OperatorEnum.Includes:
                return request.value.includes(rule.value);
            case OperatorEnum.DoesNotInclude:
                return !request.value.includes(rule.value);
            case OperatorEnum.StartsWith:
                if (Array.isArray(rule.value)) {
                    return rule.value.some(val => request.value.startsWith(val));
                }
                return request.value.startsWith(rule.value);
            case OperatorEnum.DoesNotStartWith:
                if (Array.isArray(rule.value)) {
                    return !rule.value.some(val => request.value.startsWith(val));
                }
                return !request.value.startsWith(rule.value);
            case OperatorEnum.EndsWith:
                return request.value.endsWith(rule.value);
            case OperatorEnum.DoesNotEndWith:
                return !request.value.endsWith(rule.value);
            case OperatorEnum.Field1PercentageGteField2:
                return (request.value * Number(rule.value) / 100) >= request.value2;
            case OperatorEnum.Field1SumField2GtParameter:
                return (Number(request.value) + Number(request.value2)) > Number(rule.value);
            case OperatorEnum.Field1MinusField2GtParameter:
                return (Number(request.value) - Number(request.value2)) > Number(rule.value);
            case OperatorEnum.Field1GteField2Percentage:
                return request.value >= (request.value2 * Number(rule.value) / 100);
            default:
                console.log('bad operator'); // TODO: delete this when the feasibility rules are well defined
                return false;
        }
    }

    // #region Credit
    async calculateCredit(odIncome: number, obQuarterlyIncome: number): Promise<void> {
        const req: FreelancerFeasibilityRequest = { odIncome, obQuarterlyIncome };
        // const req: FreelancerFeasibilityRequest = { odIncome: 58304, obQuarterlyIncome: 4530 };
        // const req: FreelancerFeasibilityRequest = { odIncome: 58304, obQuarterlyIncome: 1200 };
        // const req: FreelancerFeasibilityRequest = { odIncome: 58304, obQuarterlyIncome: 850 };
        this.freelancerFeasibilityResult = await this.apiService.post<FreelancerFeasibilityResult>('requests/feasibility', req);
    }

    resetFreelancerFeasibilityResult(): void {
        this.freelancerFeasibilityResult = {
            odIncomeUnroundedLimit: 0,
            odIncomeRoundedLimit: 0,
            percentage: 0,
            pass: false,
            obQuarterlyIncomeUnroundedLimit: 0,
            obQuarterlyIncomeRoundedLimit: 0,
            ok: false,
            ultimateRoundedLimit: 0
        };
    }

    private async handleCreditOperation(isGranted: boolean): Promise<void> {
        this.freelancerRequestDetailService.isLoading = true;
        const now = this.datePipe.transform(new Date(), 'dd-MM-yyyy HH:mm:ss');
        const formValues = this.feasibilityForm.value;
        const request: SaveJsonRequest = {
            queryId: this.freelancerRequestDetailService.queryId,
            data: {
                IngresosElegibles: formValues.IngresosElegibles,
                GastosComprometidos: formValues.GastosComprometidos,
                ScoreOpenBanking: formValues.ScoreOpenBanking,
                IngresosRecurrentesTrimestrales: formValues.IngresosRecurrentesTrimestrales,
                ViabilidadOpenBanking: formValues.ViabilidadOpenBanking,
                ImporteDeudaTotalCirculanteCIRBE: formValues.ImporteDeudaTotalCirculanteCIRBE,
                ImporteMoraCIRBE: formValues.ImporteMoraCIRBE,
                Viable: isGranted,
                Fecha: now.toString(),
                LimiteInicial: this.freelancerFeasibilityResult.odIncomeRoundedLimit,
                CalculoDelLimite: this.isFeasible ? this.freelancerFeasibilityResult.ultimateRoundedLimit : 0,
                rules: this.generateSimpleFeasibilityRules()
            },
            filename: FEASIBILITY_JSON_FILE_NAME
        };
        try {
            this.freelancerRequestDetailService.isCalculateViabilitySidebarVisible = false;
            await this.apiService.post<SaveJsonRequest>('requests/save-json', request);
            await this.freelancerRequestDetailService.refreshRequest(true);
            this.toastService.showSuccess('TOAST.SUCCESS', 'REQUEST_DETAIL.OPERATION_SUCCESS', true);
        } catch (error) {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.OPERATION_ERROR', true);
            console.error('Error saving feasibility:', error);
        } finally {
            this.freelancerRequestDetailService.isLoading = false;
        }
    }

    private generateSimpleFeasibilityRules(): FeasibilityRuleSimple[] {
        const simpleRules: FeasibilityRuleSimple[] = [];

        for (const rule of this.ruleFeasibilityResults) {
            const simpleRule: FeasibilityRuleSimple = {
                ruleConcept: rule.ruleConcept,
                ruleDescription: rule.ruleDescription,
                translationLabel: rule.translationLabel,
                translationVersion: rule.translationVersion,
                isFeasible: rule.isFeasible
            };
            simpleRules.push(simpleRule);
        }
        return simpleRules;
    }

    async denyCredit(): Promise<void> {
        await this.handleCreditOperation(false);
    }

    async grantCredit(): Promise<void> {
        await this.handleCreditOperation(true);
    }
    // #endregion

    // #region Validators
    private validateEquals(requestValue: any, ruleValue: any): boolean {
        // strings or arrays
        if (typeof requestValue === 'string' && typeof ruleValue === 'string') {
            if (ruleValue.includes('::')) {
                const ruleValuesArray = ruleValue.split('::');
                return ruleValuesArray.includes(requestValue);
            } else {
                return requestValue === ruleValue;
            }
        }

        //booleans
        if (typeof requestValue === 'boolean' ){
            return requestValue === Boolean(ruleValue);
        }

        // numbers
        const requestValueNumber = Number(requestValue);
        const ruleValueNumber = Number(ruleValue);
        if (!isNaN(requestValueNumber) || !isNaN(ruleValueNumber)) {
            return requestValueNumber === ruleValueNumber;
        }

        // dates
        const requestDate = new Date(requestValue);
        const ruleDate = new Date(ruleValue);
        if (!isNaN(requestDate.getTime()) && !isNaN(ruleDate.getTime())) {
            return requestDate === ruleDate;
        }

        // any other type
        return false;
    }

    private validateLessThan(requestValue: any, ruleValue: any): boolean {
        // numbers
        const requestValueNumber = Number(requestValue);
        const ruleValueNumber = Number(ruleValue);
        if (!isNaN(requestValueNumber) || !isNaN(ruleValueNumber)) {
            return requestValueNumber < ruleValueNumber;
        }

        // dates
        const requestDate = new Date(requestValue);
        const ruleDate = new Date(ruleValue);
        if (!isNaN(requestDate.getTime()) && !isNaN(ruleDate.getTime())) {
            return requestDate < ruleDate;
        }

        // any other type
        return false;
    }

    private validateLessOrEqualThan(requestValue: any, ruleValue: any): boolean {
        // numbers
        const requestValueNumber = Number(requestValue);
        const ruleValueNumber = Number(ruleValue);
        if (!isNaN(requestValueNumber) || !isNaN(ruleValueNumber)) {
            return requestValueNumber <= ruleValueNumber;
        }

        // dates
        const requestDate = new Date(requestValue);
        const ruleDate = new Date(ruleValue);
        if (!isNaN(requestDate.getTime()) && !isNaN(ruleDate.getTime())) {
            return requestDate <= ruleDate;
        }

        // any other type
        return false;
    }

    private validateGreaterThan(requestValue: any, ruleValue: any): boolean {
        // numbers
        const requestValueNumber = Number(requestValue);
        const ruleValueNumber = Number(ruleValue);
        if (!isNaN(requestValueNumber) || !isNaN(ruleValueNumber)) {
            return requestValueNumber > ruleValueNumber;
        }

        // dates
        const requestDate = new Date(requestValue);
        const ruleDate = new Date(ruleValue);
        if (!isNaN(requestDate.getTime()) && !isNaN(ruleDate.getTime())) {
            return requestDate > ruleDate;
        }

        // any other type
        return false;
    }

    private validateGreaterOrEqualThan(requestValue: any, ruleValue: any): boolean {
        // numbers
        const requestValueNumber = Number(requestValue);
        const ruleValueNumber = Number(ruleValue);
        if (!isNaN(requestValueNumber) || !isNaN(ruleValueNumber)) {
            return requestValueNumber >= ruleValueNumber;
        }

        // dates
        const requestDate = new Date(requestValue);
        const ruleDate = new Date(ruleValue);
        if (!isNaN(requestDate.getTime()) && !isNaN(ruleDate.getTime())) {
            return requestDate >= ruleDate;
        }

        // any other type
        return false;
    }

    private positiveNumberValidator(): ValidatorFn {
        return (control: AbstractControl): Record<string, boolean> | null => {
            const value = control.value;
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const numberValue = Number(value);
            if (isNaN(numberValue) || numberValue < 0) {
                return { 'positiveNumber': true };
            }
            return null;
        };
    }
    // #endregion

    // #region Translation
    translateRuleConcept(rule: FeasibilityRule | FeasibilityRuleSimple): Observable<string> {
	    return this.translateService.get(`RULES.${rule.translationLabel}.${rule.translationVersion}.CONCEPT`);
    }

    translateRuleDescription(rule: FeasibilityRule | FeasibilityRuleSimple): Observable<string> {
	    return this.translateService.get(`RULES.${rule.translationLabel}.${rule.translationVersion}.DESCRIPTION`);
    }
    // #endregion
}
